import { formatHashtag } from "@caliu/utils";
import { formatDate, formatDateTime } from "./format";
import { tagFullPath, type HierarchicalTag } from "./tagHierarchy";

/**
 * How a note created in the app starts (Settings → Editor → "New notes
 * start with"). Stored under `caliu-new-note-start`, the same key and values
 * as the Swift and Android apps. In display order:
 *
 * - `h1` to `h4`: an empty heading of that level, caret in it.
 * - `date`, `date-iso`, `datetime`, `datetime-iso`: an H1 with the creation
 *   date (and time), caret in the empty paragraph below.
 * - `empty`: nothing, caret in the first paragraph.
 */
export const NEW_NOTE_STARTS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "date",
  "date-iso",
  "datetime",
  "datetime-iso",
  "empty",
] as const;

export type NewNoteStart = (typeof NEW_NOTE_STARTS)[number];

/** What an unknown or old stored value (`title`, `body`, `timestamp`) reads as. */
export const DEFAULT_NEW_NOTE_START: NewNoteStart = "h1";

export function isNewNoteStart(value: unknown): value is NewNoteStart {
  return (NEW_NOTE_STARTS as readonly unknown[]).includes(value);
}

/** The block a mode starts with, which is where the caret goes. */
export type NewNoteStartKind = "heading" | "date" | "empty";

export function newNoteStartKind(mode: NewNoteStart): NewNoteStartKind {
  switch (mode) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
      return "heading";
    case "date":
    case "date-iso":
    case "datetime":
    case "datetime-iso":
      return "date";
    case "empty":
      return "empty";
  }
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The text a date mode's heading shows, or `null` for the other
 * modes. The ISO forms are a fixed ASCII `yyyy-MM-dd` (and ` HH:mm`) in the
 * device's time zone; the others are `Intl` long date (and short time) in the
 * app's UI language.
 */
export function newNoteDateText(mode: NewNoteStart, createdAt: Date): string | null {
  const isoDate = () =>
    `${createdAt.getFullYear()}-${pad(createdAt.getMonth() + 1)}-${pad(createdAt.getDate())}`;
  switch (mode) {
    case "date":
      return formatDate(createdAt, { dateStyle: "long" });
    case "date-iso":
      return isoDate();
    case "datetime":
      return formatDateTime(createdAt, { dateStyle: "long", timeStyle: "short" });
    case "datetime-iso":
      return `${isoDate()} ${pad(createdAt.getHours())}:${pad(createdAt.getMinutes())}`;
    default:
      return null;
  }
}

/** The full path (`work/project`) of the tag a tag view creates notes in. */
export function newNoteTagPath<T extends HierarchicalTag>(
  tagId: string | null | undefined,
  tags: readonly T[],
): string | undefined {
  if (!tagId) return undefined;
  const tag = tags.find((t) => t.id === tagId);
  if (!tag) return undefined;
  return tagFullPath(tag, new Map(tags.map((t) => [t.id, t])));
}

/**
 * The markdown a note created in the app is stored with. Every web entry
 * point (the list "+", the shortcut, the command palette) uses it.
 *
 * - `h1` to `h4`: an empty heading (`# ` to `#### `). In a tag view, an empty
 *   paragraph and the hashtag line follow.
 * - Date modes: an H1 with {@link newNoteDateText} as plain text (a heading's
 *   text is inline, so `# 20. Oktober 2026` is no list item to the title
 *   extractors), and a blank-line run, which gives the empty paragraph before
 *   the hashtag line. Without a tag the run is trailing and parses to nothing,
 *   so the editor adds the paragraph on first load (`planNewNoteStart`).
 * - `empty`: nothing, so the editor opens on an empty paragraph. In a tag view
 *   the stored text is only the hashtag line: no markdown parses to a LEADING
 *   empty paragraph (the blank-line contract drops a leading run of blank
 *   lines), so the editor inserts that paragraph on first load.
 *
 * The hashtag is the tag's full path (`#work/project`), in the closed form
 * for a multi-word tag (`#my project#`).
 */
export function newNoteContent(
  mode: NewNoteStart,
  tagPath: string | undefined,
  createdAt: Date,
): string {
  const hashtag = tagPath ? formatHashtag(tagPath) : "";
  switch (newNoteStartKind(mode)) {
    case "heading": {
      const marker = `${"#".repeat(Number(mode.slice(1)))} `;
      return hashtag ? `${marker}\n\n\n${hashtag}` : marker;
    }
    case "date":
      return `# ${newNoteDateText(mode, createdAt) ?? ""}\n\n\n${hashtag}`;
    case "empty":
      return hashtag;
  }
}
